import { FC, useEffect, useMemo, useState } from 'react';
import { List } from 'antd';
import { StyledListItemContent } from 'theme/styled';
import {
  AppointmentFileRow,
  LegacyAppointment,
  fetchAppointments,
  fetchPreviousFilesByPatient
} from 'services/api/appointments';

import FileItem from './FileItem';
import { parseJson } from 'utils/json';

// links is jsonb on the wire — usually a string[], occasionally a JSON string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseLinks = (links: any): string[] => {
  const value = parseJson(links);

  return Array.isArray(value) ? value : [];
};

interface IProps {
  id: string;
  setTotal(total: number): void;
}

const Files: FC<IProps> = ({ id, setTotal }) => {
  const [linkAppointments, setLinkAppointments] = useState<LegacyAppointment[]>(
    []
  );

  const [linksLoading, setLinksLoading] = useState(true);

  useEffect(() => {
    setLinksLoading(true);
    // appointment attached-links for this patient (legacy
    // PatientAppointmentsLinks) — the appointments list carries `links`
    fetchAppointments({ patient: id, page: 1, limit: 9999 })
      .then(res => setLinkAppointments(res.results))
      .catch(() => setLinkAppointments([]))
      .finally(() => setLinksLoading(false));
  }, [id]);

  const [previousFiles, setPreviousFiles] = useState<AppointmentFileRow[]>([]);
  const [filesLoading, setFilesLoading] = useState(true);

  useEffect(() => {
    setFilesLoading(true);
    fetchPreviousFilesByPatient(id)
      .then(setPreviousFiles)
      .catch(() => setPreviousFiles([]))
      .finally(() => setFilesLoading(false));
  }, [id]);

  const filesResults = useMemo(
    () =>
      previousFiles.map(item => ({
        file: item.file,
        created_at: item.createdAt || '',
        appointment_id: String(item.appointmentId),
        link: ''
      })),
    [previousFiles]
  );

  const linkResults = useMemo(
    () =>
      linkAppointments
        .map(
          item =>
            (parseLinks(item.links) || []).map((link: string) => ({
              file: '',
              created_at: '',
              appointment_id: String(item.id),
              link
            })) || []
        )
        ?.flat() || [],
    [linkAppointments]
  );

  useEffect(() => {
    if (filesResults.length || linkResults.length) {
      setTotal((filesResults.length || 0) + (linkResults.length || 0));
    }
  }, [filesResults.length, linkResults.length, setTotal]);

  return (
    <StyledListItemContent>
      <List
        loading={filesLoading || linksLoading}
        dataSource={[...filesResults, ...linkResults]}
        renderItem={item => (
          <List.Item className="list-content-item">
            <FileItem
              date={item.created_at}
              link={item.link}
              file={item.file}
              appointmentId={item.appointment_id}
            />
          </List.Item>
        )}
      />
    </StyledListItemContent>
  );
};

export default Files;
